/** Current credit balance: total plus the purchased / free split */

import { Coins } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { cardClass, errorTextClass, hintClass } from '../ui/styles'
import { cn } from '../../lib/utils'
import type { CreditBalance } from '../../hooks/use-credits'

interface Props {
  balance: CreditBalance | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function CreditBalanceCard({ balance, isLoading, isError, onRetry }: Props) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const fmt = (n: number | undefined) => (isLoading || n === undefined ? '…' : new Intl.NumberFormat(locale).format(n))

  if (isError && !balance) {
    return (
      <div className={errorTextClass} role="alert">
        {t('billing.balance.error')}{' '}
        <button type="button" onClick={onRetry} className="cursor-pointer font-medium underline underline-offset-2">
          {t('common.retry')}
        </button>
      </div>
    )
  }

  return (
    <div className={cn(cardClass, 'p-5')}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm text-fg-muted">
            <Coins className="h-4 w-4 text-accent-text" aria-hidden="true" />
            {t('billing.balance.total')}
          </p>
          <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-fg" data-testid="credit-total">
            {t('billing.balance.credits', { count: fmt(balance?.totalCredits) })}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-6 text-sm">
          <div>
            <dt className="text-fg-subtle">{t('billing.balance.purchased')}</dt>
            <dd className="mt-0.5 font-medium text-fg">{fmt(balance?.purchasedCredits)}</dd>
          </div>
          <div>
            <dt className="text-fg-subtle">{t('billing.balance.free')}</dt>
            <dd className="mt-0.5 font-medium text-fg">{fmt(balance?.freeCredits)}</dd>
          </div>
        </dl>
      </div>
      <p className={cn(hintClass, 'mt-4')}>{t('billing.balance.hint')}</p>
    </div>
  )
}
